import { createSelector } from 'reselect';
import get from 'lodash/get';
import {
  BACKEND_PERSONAL_INFO_KEY,
  BACKEND_EDUCATION_GOALS_KEY,
  BACKEND_EDUCATION_LIST_KEY,
} from './ChildrenInfoWorkflowConstants';
import {
  advWorkflow as advWorkflowSelector,
  currentClientId as currentClientIdSelector,
} from '../AdvWorkflow/AdvWorkflowSelector';
import { filterDependents } from './helpers/filterDependents';

export const hasEducationWorkflowData = state => !!state.AdvWorkflow.EducationWorkflow;

export const clientId = currentClientIdSelector;

export const educationWorkflow = createSelector(
  advWorkflowSelector,
  currentClientIdSelector,
  (advWorkflow, currentClientId) => get(advWorkflow, currentClientId, {}),
);

export const educationWorkflowData = createSelector(educationWorkflow, education => education.data);

export const educationWorkflowData2 = createSelector(
  educationWorkflow,
  education => education.data2,
);

export const getChildrenList = createSelector(educationWorkflowData, data =>
  filterDependents(data && data.PersonalInfo ? data.PersonalInfo.Dependents : []),
);

export const getSelectedChildFirstName = createSelector(educationWorkflowData2, data2 => {
  if (data2[BACKEND_PERSONAL_INFO_KEY] && data2[BACKEND_PERSONAL_INFO_KEY].SelectedChildFirstName) {
    return data2[BACKEND_PERSONAL_INFO_KEY].SelectedChildFirstName;
  }
  return '';
});

export const getInputAge = createSelector(educationWorkflowData2, data2 => {
  if (data2 && data2[BACKEND_PERSONAL_INFO_KEY]) {
    return data2[BACKEND_PERSONAL_INFO_KEY].ChildAge;
  }
  return '';
});

export const getEducationGoalList = createSelector(educationWorkflowData, data => {
  if (data && data[BACKEND_EDUCATION_GOALS_KEY]) {
    return data[BACKEND_EDUCATION_GOALS_KEY];
  }
  return [];
});

export const getChildEducationGoal = (educationGoalList, name) =>
  educationGoalList.find(goal => name === goal.Name) || {};

export const getSelectedEducationType = createSelector(educationWorkflowData2, data2 => {
  if (data2 && data2[BACKEND_EDUCATION_LIST_KEY]) {
    return data2[BACKEND_EDUCATION_LIST_KEY].EducationType;
  }
  return '';
});

export const getUpdatedEducationGoalData = createSelector(educationWorkflowData2, data2 => {
  if (data2 && data2[BACKEND_EDUCATION_LIST_KEY]) {
    return data2[BACKEND_EDUCATION_LIST_KEY];
  }
  return '';
});

export const getEducationTypeOptions = createSelector(educationWorkflowData, data => {
  if (data && data[BACKEND_EDUCATION_LIST_KEY]) {
    return data[BACKEND_EDUCATION_LIST_KEY];
  }
  return [];
});

export const getInputAmount = createSelector(educationWorkflowData2, data2 => {
  if (data2 && data2[BACKEND_EDUCATION_LIST_KEY]) {
    return data2[BACKEND_EDUCATION_LIST_KEY].Amount;
  }
  return undefined;
});
export const getInputYears = createSelector(educationWorkflowData2, data2 => {
  if (data2 && data2[BACKEND_EDUCATION_LIST_KEY]) {
    return data2[BACKEND_EDUCATION_LIST_KEY].Years;
  }
  return undefined;
});

export const getInputEnrollAge = createSelector(educationWorkflowData2, data2 => {
  if (data2 && data2[BACKEND_EDUCATION_LIST_KEY]) {
    return data2[BACKEND_EDUCATION_LIST_KEY].Age;
  }
  return undefined;
});

export const childFirstName = data => data[BACKEND_PERSONAL_INFO_KEY].SelectedChildFirstName;

export const yearToAge = year => {
  const currentDate = new Date();
  return currentDate.getFullYear() - year;
};

export const ageToYear = age => {
  const currentDate = new Date();
  return currentDate.getFullYear() - age;
};

export const getChildData = (childrenList, FirstName) =>
  childrenList.find(child => child.FirstName === FirstName);

export const isChildWithoutId = child => !(child && child.Id);

export const getGoalId = (childrenList, selectedChildFirstName, goals) => {
  const selectedChild = getChildData(childrenList, selectedChildFirstName);

  if (selectedChild && selectedChild.Id) {
    const { Id } = goals.find(goal => goal.DependentId === selectedChild.Id);
    return Id;
  }

  const childGoal = goals.reduce((goalFound, goal) => {
    const foundChildGoal = childrenList.find(child => child.Id === goal.DependentId);
    if (foundChildGoal) {
      return {};
    }
    return goal;
  }, {});

  return childGoal.Id;
};

export const childFirstNameOrDefaultSelector = createSelector(
  advWorkflowSelector,
  currentClientIdSelector,
  (advWorkflow, clientID) =>
    get(advWorkflow, `${clientID}.data2.${BACKEND_PERSONAL_INFO_KEY}.SelectedChildFirstName`, ''),
);

export const goalValueOrDefaultHigherOrderSelector = (propName, defaultValue) =>
  createSelector(
    advWorkflowSelector,
    currentClientIdSelector,
    childFirstNameOrDefaultSelector,
    (advWorkflow, clientID, childFirstNameOrDefault) =>
      get(
        advWorkflow,
        `${clientID}.data2.${BACKEND_EDUCATION_LIST_KEY}[${propName}]`,
        get(advWorkflow, `${clientID}.data.${BACKEND_EDUCATION_GOALS_KEY}`, []).reduce(
          (current, { Name, ...rest }) =>
            Name === childFirstNameOrDefault ? rest[propName] : current,
          defaultValue,
        ),
      ),
  );

export const enrollAgeOrDefaultSelector = goalValueOrDefaultHigherOrderSelector('Age', '');
export const isSpecificCollegeOrDefaultSelector = goalValueOrDefaultHigherOrderSelector(
  'IsSpecificCollege',
  '',
);
export const collegeNameOrDefaultSelector = goalValueOrDefaultHigherOrderSelector(
  'SpecificCollegeName',
  '',
);
export const specificCollegeIsPublicOrDefaultSelector = goalValueOrDefaultHigherOrderSelector(
  'SpecificCollegeIsPublic',
  false,
);
export const specificCollegeInformationOrDefaultSelector = goalValueOrDefaultHigherOrderSelector(
  'SpecificCollegeInformation',
  false,
);

export const hasTuitionTypeOrDefaultSelector = createSelector(
  specificCollegeIsPublicOrDefaultSelector,
  specificCollegeInformationOrDefaultSelector,
  (specificCollegeIsPublicOrDefault, specificCollegeInformationOrDefault) =>
    !!(specificCollegeIsPublicOrDefault && specificCollegeInformationOrDefault),
);
export const durationOrDefaultSelector = goalValueOrDefaultHigherOrderSelector('Years', '');
export const tuitionTypeOrDefaultSelector = createSelector(
  goalValueOrDefaultHigherOrderSelector('SpecificCollegeIsInState', ''),
  specificCollegeIsInState => (specificCollegeIsInState ? 'In-State' : 'Out-Of-State'),
);

export const expectedTuitionOrDefaultSelector = goalValueOrDefaultHigherOrderSelector('Amount', 0);

export const educationTypeOrDefaultSelector = goalValueOrDefaultHigherOrderSelector(
  'EducationType',
  null,
);

export const educationTypeOptionsOrDefaultSelector = createSelector(
  advWorkflowSelector,
  currentClientIdSelector,
  (advWorkflow, currentClientId) =>
    get(advWorkflow, `${currentClientId}.data.${BACKEND_EDUCATION_LIST_KEY}`, []),
);

export const schoolTypeOrDefaultSelector = createSelector(
  educationTypeOrDefaultSelector,
  educationTypeOptionsOrDefaultSelector,
  (type, options) =>
    options
      .reduce((acc, { value, label }) => (value === type ? label : acc), '')
      .split(/[ ][\\(]/)[0],
);

export const childrenListOrDefaultSelector = createSelector(
  advWorkflowSelector,
  currentClientIdSelector,
  (advWorkflow, clientID) =>
    filterDependents(
      get(advWorkflow, `${clientID}.data.${BACKEND_PERSONAL_INFO_KEY}.Dependents`, []),
    ),
);
